<template>
  <div class="account-list">
    <h3 class="account-list-title">请选择需要登录的账号和角色</h3>
    <md-table
      :data="roleGroupData"
      highlight-current-row
      style="width: 100%"
      height="220"
      size="default"
      @current-change="handleCurrentChange"
    >
      <md-table-column width="48">
        <template #default="scope">
          <md-radio
            v-model="curAccount"
            size="large"
            :label="scope.row.userCode + scope.row.roleCode"
            @change="handleRadioChange(scope.row)"
          />
        </template>
      </md-table-column>
      <md-table-column property="userCode" show-overflow-tooltip label="账号" width="140" />
      <md-table-column property="userName" show-overflow-tooltip label="账号名称" width="200" />
      <md-table-column property="roleName" show-overflow-tooltip label="角色" width="200" />
    </md-table>
    <div class="account-list-btn">
      <md-button size="large" @click="backLogin">返回登录</md-button>
      <md-button size="large" type="primary" :loading="loading" @click="handleLogin">确定</md-button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, defineProps, defineEmits, toRaw, onMounted, defineExpose } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { MdTable } from 'mdesign3';
import { MdMessage } from 'mdesign3';
import config from '@/config/user.env';
import { ILoginBySmsParams, IStorageResourceParams } from '@/utils/interface';
import { getControlParam } from '@/api/userApi';
import { storageResource, preInitSystem } from './utilsLogin';
import { prefetchUserApplications, prefetchUserRoleResources } from '@/utils/roleResourcesCache';
const route = useRoute();
const router = useRouter();

const props = defineProps<{
  loginMode: string;
  accountData: Array<any>;
  accountRoleDatas: Array<any>;
  accountParams: Object<any>;
  storageResourceParams: IStorageResourceParams;
  handleLoginFn: (data: any, cb: (err?: unknown) => void) => void;
}>();
const curRole = ref<String>('');
const curAccount = ref<String>('');
const curUserCode = ref<String>('');
const loading = ref<boolean>(false);
const roleGroupData = ref<Array<any>>([]);
/** 仅预取角色资源接口；首页子应用已在登录页预好，选角色不再 preFetch（多角色弹窗期间给预取留提前量） */
const prefetchRoleResources = (roleCode: string, userCode?: string) => {
  const code = userCode || props.storageResourceParams?.userInfo?.userCode;
  if (code && roleCode) {
    prefetchUserRoleResources(code, roleCode);
  }
};

const handleRadioChange = (row) => {
  curRole.value = row.roleCode;
  curUserCode.value = row.userCode;
  prefetchRoleResources(row.roleCode, row.userCode);
};

const handleCurrentChange = (row) => {
  curAccount.value = row.userCode + row.roleCode;
  handleRadioChange(row);
};

const emit = defineEmits(['onBackLogin']);
const backLogin = () => {
  emit('onBackLogin');
};

const navigateAfterLogin = async () => {
  const initialized = await preInitSystem();
  if (!initialized) return;
  if (route.query.redirectUrl) {
    router.replace(`${decodeURIComponent(route.query.redirectUrl as string)}`);
  } else {
    router.replace('/');
  }
};

// 登录
const handleLogin = async () => {
  if (!curRole.value) {
    MdMessage.error('请选择需要登录的账号');
    return;
  }
  if (loading.value) return;

  if (props.loginMode === 'SMS') {
    const { mobile = '', verificationCode = '' } = props.accountParams;
    const tableList = toRaw(roleGroupData.value);
    const data: ILoginBySmsParams & IStorageResourceParams = {
      mobile,
      verificationCode,
      userCode: curUserCode.value,
      applicationCode: config.applicationCode,
      roleCode: curRole.value,
      roleList: tableList, // 角色列表
    };
    loading.value = true;
    const accountDatas = toRaw(props.accountData);
    let keepLoading = false;
    try {
      if (accountDatas.length === 1) {
        const storageResourceParams: IStorageResourceParams = {
          roleCode: curRole.value,
          roleList: tableList,
          userInfo: accountDatas[0],
          currentTenant: accountDatas[0].currentTenant,
        };
        await storageResource(storageResourceParams);
        await navigateAfterLogin();
      } else {
        keepLoading = true;
        props.handleLoginFn(data, async (err?: unknown) => {
          try {
            if (!err) {
              await navigateAfterLogin();
            }
          } finally {
            loading.value = false;
          }
        });
        return;
      }
    } finally {
      if (!keepLoading) {
        loading.value = false;
      }
    }
  } else {
    loading.value = true;
    try {
      const storageResourceParams = toRaw(props.storageResourceParams);
      storageResourceParams.roleCode = curRole.value;
      prefetchRoleResources(curRole.value as string);
      await storageResource(storageResourceParams);
      await navigateAfterLogin();
    } finally {
      loading.value = false;
    }
  }
};

// 获取角色组，临时方案
const getControlParamFn = async () => {
  let roleList = [];

  try {
    const params = {
      applicationCode: config.applicationCode,
      code: 'EL_ROLE_GROUP',
    };
    const res = await getControlParam(params);
    const roleGroupList = res.data?.value;
    let roleGroup = JSON.parse(roleGroupList);
    roleGroup =
      roleGroup &&
      roleGroup.map((item) =>
        item.roleGroupValue.map((e) => {
          e.roleGroupCode = item.roleGroupCode;
          return e;
        }),
      );
    roleList = roleGroup && roleGroup.flat();
  } catch (error) {
    roleList = [];
  }

  const roleCodeArr = roleList && roleList.map((item) => item.roleCode);

  if (roleCodeArr && roleCodeArr.length > 0) {
    let tableData = [];
    const tableList = toRaw(props.accountRoleDatas);
    let innerGroup = tableList.filter((item) => roleCodeArr.includes(item.roleCode));
    const outerGroup = tableList.filter((item) => !roleCodeArr.includes(item.roleCode));

    if (innerGroup && innerGroup.length > 0) {
      innerGroup = innerGroup.map((item) => {
        item.roleName = roleList.find((role) => role.roleCode === item.roleCode)?.roleGroupName || item.roleName;
        item.roleGroupCode = roleList.find((role) => role.roleCode === item.roleCode)?.roleGroupCode;
        return item;
      });

      const tableListArr = innerGroup.map((item) => item.userCode);
      let uniques = tableListArr.filter((value, index, self) => self.indexOf(value) === index);

      if (uniques && uniques.length > 0) {
        uniques = uniques.map((item) => {
          const roleArr = innerGroup.filter((role) => role.userCode === item);
          const roleCodes = roleArr.map((role) => role.roleCode);
          const roleObj = roleArr && roleArr[0];
          roleObj.roleCode = roleCodes.join(',');
          return roleObj;
        });
      }

      tableData = [...uniques, ...outerGroup];
    } else {
      tableData = [...tableList];
    }
    roleGroupData.value = tableData;
  } else {
    roleGroupData.value = props.accountRoleDatas;
  }
  if (roleGroupData.value.length === 1 && props.loginMode === 'SMS') {
    curUserCode.value = roleGroupData.value[0].userCode;
    curRole.value = roleGroupData.value[0].roleCode;
    handleLogin();
  } else if (roleGroupData.value.length > 1 && props.loginMode === 'SMS') {
    emit('showAccountList', true);
  }
};
defineExpose({
  getControlParamFn,
});
onMounted(() => {
  if (props.loginMode === 'SMS') {
    getControlParamFn();
  } else {
    const { roleList, userInfo } = props.storageResourceParams;
    roleGroupData.value = roleList;
    if (userInfo?.userCode) {
      prefetchUserApplications(userInfo.userCode);
    }
  }
});
</script>

<style scoped lang="less">
.account-list {
  width: 568px;
  height: 380px;
  padding: 24px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0px 2px 8px 0px rgba(0, 21, 41, 0.06);
  .account-list-title {
    margin-bottom: 16px;
    text-align: center;
    color: #1d2129;
    font-size: 16px;
    font-weight: 500;
  }
  :deep(.md-table) {
    .md-table__row {
      .cell {
        display: flex;
        align-items: center;
      }
    }
    .md-table__inner-wrapper::before {
      display: none;
    }
    .md-table__cell {
      padding: 10px 0;
    }
    .md-radio {
      height: 18px;
      .md-radio__inner {
        width: 18px;
        height: 18px;
      }
    }
    .md-table__header-wrapper {
      border-radius: 4px 4px 0 0;
    }
  }

  .account-list-btn {
    margin-top: 32px;
    display: flex;
    justify-content: center;
    :deep(.md-button) {
      width: 104px;
      border-radius: 4px;
      &.md-button--primary {
        margin-left: 16px;
      }
    }
  }
  :deep(.md-radio__label) {
    display: none;
  }
}
</style>
